import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Users, Mail, Phone, Download, Search } from 'lucide-react';

import { queryKeys } from '@/lib/api/queryKeys';
import { contactsApi } from './api';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { HelpHint } from '@/features/help/HelpHint';
import type { CoContactFilters } from '@/lib/api/types';

/**
 * Caso 01 — Leads CRM. Replaces the legacy mock-grouped Responses view
 * with a real CRM-grade contact table sourced from /co-contacts. The page
 * unit IS the person (deduped by email or phone), not the response row.
 */
export function ContactsPage() {
  const t = useT();
  useFavoritable(t('contacts.title'));
  const navigate = useNavigate();

  const [filters, setFilters] = useState<CoContactFilters>({
    page: 1,
    limit: 20,
  });
  const [searchInput, setSearchInput] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.contacts.list(
      filters as unknown as Record<string, unknown>,
    ),
    queryFn: () => contactsApi.list(filters),
    placeholderData: keepPreviousData,
  });

  function applySearch() {
    setFilters((f) => ({ ...f, q: searchInput || undefined, page: 1 }));
  }

  function toggleConsent() {
    setFilters((f) => ({
      ...f,
      consent: f.consent ? undefined : true,
      page: 1,
    }));
  }

  function setPage(page: number) {
    setFilters((f) => ({ ...f, page }));
  }

  async function downloadCsv() {
    const blob = await contactsApi.exportCsv(filters);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contacts-${dayjs().format('YYYYMMDD-HHmm')}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const page = filters.page ?? 1;
  const limit = filters.limit ?? 20;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="page space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold">
            {t('contacts.title')}
            <HelpHint section="contacts-list" />
          </h1>
          <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
            {t('contacts.subtitle')}
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={downloadCsv}
          disabled={total === 0}
        >
          <Download className="mr-2 h-4 w-4" />
          {t('contacts.export')}
        </Button>
      </header>

      <Card>
        <CardContent className="flex flex-wrap items-center gap-2 pt-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-fg-subtle)]" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applySearch()}
              placeholder={t('contacts.searchPlaceholder')}
              className="w-full rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-9 py-2 text-sm"
            />
          </div>
          <Button variant="secondary" onClick={applySearch}>
            {t('contacts.applySearch')}
          </Button>
          <Button
            variant={filters.consent ? 'primary' : 'secondary'}
            onClick={toggleConsent}
          >
            {t('contacts.filter.consentOnly')}
          </Button>
        </CardContent>
      </Card>

      {isLoading ? (
        <Skeleton className="h-40" />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Users className="h-6 w-6" />}
          title={t('contacts.empty.title')}
          description={t('contacts.empty.body')}
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>
              {t('contacts.totalCount', { count: total })}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-[var(--color-fg-subtle)]">
                <tr>
                  <th className="py-2 text-left font-semibold">
                    {t('contacts.col.name')}
                  </th>
                  <th className="py-2 text-left font-semibold">
                    {t('contacts.col.contact')}
                  </th>
                  <th className="py-2 text-right font-semibold tabular-nums">
                    {t('contacts.col.interactions')}
                  </th>
                  <th className="py-2 text-right font-semibold tabular-nums">
                    {t('contacts.col.prizes')}
                  </th>
                  <th className="py-2 text-left font-semibold">
                    {t('contacts.col.lastSeen')}
                  </th>
                  <th className="py-2 text-left font-semibold">
                    {t('contacts.col.consent')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => {
                  const name =
                    [c.firstName, c.lastName].filter(Boolean).join(' ').trim() ||
                    t('contacts.anonymousName');
                  return (
                    <tr
                      key={c.id}
                      className="cursor-pointer border-t border-[var(--color-border-subtle)] hover:bg-[var(--color-bg-elevated)]"
                      onClick={() => navigate(`/contacts/${c.id}`)}
                    >
                      <td className="py-3 font-medium">{name}</td>
                      <td className="py-3 text-[var(--color-fg-muted)]">
                        <ContactSnippet
                          email={c.email}
                          phone={c.phone}
                          placeholder={t('contacts.anonymousSnippet')}
                        />
                      </td>
                      <td className="py-3 text-right tabular-nums">
                        {c.interactionsCount}
                      </td>
                      <td className="py-3 text-right tabular-nums">
                        {c.prizesWonCount}
                      </td>
                      <td className="py-3">
                        {c.lastSeenAt
                          ? dayjs(c.lastSeenAt).format('MMM D, YYYY')
                          : '—'}
                      </td>
                      <td className="py-3">
                        {c.marketingConsent ? (
                          <Badge tone="success">
                            {t('contacts.consent.yes')}
                          </Badge>
                        ) : (
                          <Badge tone="neutral">
                            {t('contacts.consent.no')}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pagination
              page={page}
              pageCount={totalPages}
              total={total}
              from={total === 0 ? 0 : (page - 1) * limit + 1}
              to={Math.min(page * limit, total)}
              setPage={setPage}
              next={() => setPage(Math.min(page + 1, totalPages))}
              prev={() => setPage(Math.max(page - 1, 1))}
              noun={t('contacts.noun')}
            />

          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ContactSnippet(props: {
  email: string | null;
  phone: string | null;
  placeholder: string;
}) {
  if (!props.email && !props.phone) {
    return (
      <span className="text-[var(--color-fg-subtle)]">{props.placeholder}</span>
    );
  }
  return (
    <span className="inline-flex flex-col gap-0.5">
      {props.email && (
        <span className="inline-flex items-center gap-1">
          <Mail className="h-3 w-3" />
          {props.email}
        </span>
      )}
      {props.phone && (
        <span className="inline-flex items-center gap-1">
          <Phone className="h-3 w-3" />
          {props.phone}
        </span>
      )}
    </span>
  );
}
